import React, {Component} from 'react';
import {LayoutSidebar} from "../../Layout";
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import {AgencyHeader} from "./AgencyHeader";
import {InviteUserModal, EditUserModalInline} from "../invitations/InviteUserModal";
import {Spinner} from "../Spinner";
import {FaIcon} from "../FaIcon";
import {DeleteUserModal} from "./DeleteUserModal";
import {DeleteInvitationModal} from "./DeleteInvitationModal";
import {LocalStorage} from "../../utils/LocalStorage";
import {AgencyTabs} from "./AgencyTabs";
import {EditClientFeaturesModal} from "./EditClientFeaturesModal";
import {RolePill} from "../elements/RolePill";
import {LoadErrorState} from "../elements/LoadErrorState";
import {withRouterParams} from "../../utils/withRouterParams";
import {isOk} from "../../utils/apiResponse";
import {useUiVersion} from "../../context/UserModulesContext";
import {LearnTeamApi} from "../../api/learnTeamApi";
import {anchorAttr, guideLoadingAttr} from "../../guides/anchors";
import {TeamLearningHead, TeamLearningTable} from "./team_learning/TeamLearning";
import { withTranslation } from 'react-i18next';
import {toast} from 'react-toastify';

// Cuánto espera la lista del equipo a los pedidos de Aprende Dashbo antes de dibujarse sin avance. El
// `APIClient` no tiene timeout, y la lista es lo principal de la pantalla (review C-apr-fe-equipo-04).
export const LEARNING_WAIT_MS = 3500;

class AgencyTeam extends Component {
    render() {
        return (
            <div className="app-content">
                <LayoutSidebar/>
                <AgencyTeamContentInFrame agencyId={this.props.match.params.id} />
            </div>
        )
    }
}

// «Aprende Dashbo» vive sólo en la 2.1 (contrato §1 de la tanda): en el clásico la pantalla queda
// como siempre, sin pedir nada de `learning/`.
function AgencyTeamContentInFrame(props) {
    const {isV2} = useUiVersion();
    return <AgencyTeamContent {...props} isV2={isV2}/>;
}


class AgencyTeamContentClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            team: null,
            invitations: null,
            canEditTeam: null,
            canAssignClients: null,
            canAddClientUser: true,
            canInviteClientUser: false,
            isSuperAdmin: false,
            moduleAccessConfigurationEnabled: null,
            clientUsersLimitInfo: null,
            // `null` = sin error; string = la carga de `agency/team` falló (patrón LoadErrorState).
            loadError: null,
            // Aprende Dashbo: `learningAccess` = `{enabled, isAdmin}` de `learning/me/`; `learning` =
            // el payload de `learning/team/`, sólo si respondió OK (o sea, Admin con la función prendida).
            learningAccess: null,
            learning: null,
            learningResolved: false,
        };
        this.refreshUserName = this.refreshUserName.bind(this);
        this.removeDeletedUser = this.removeDeletedUser.bind(this);
        this.removeDeletedInvitation = this.removeDeletedInvitation.bind(this);
        this.handleNewUserWasInvited = this.handleNewUserWasInvited.bind(this);
        this.reloadAfterEdit = this.reloadAfterEdit.bind(this);
        this.retryLoad = this.retryLoad.bind(this);
    }

    componentDidMount() {
        this.updateTeam();
        this.loadLearning();
    }

    componentDidUpdate(prevProps) {
        // El marco puede resolverse después del montaje (cache de módulos vacío).
        if (this.props.isV2 && !prevProps.isV2) this.loadLearning();
        // «Volver al Dashbo clásico» no remonta la pantalla (`setUiVersion` sólo cambia el contexto): el
        // avance se olvida, y se vuelve a pedir si regresa a la 2.1 (review C-apr-fe-equipo-01).
        if (!this.props.isV2 && prevProps.isV2) this.forgetLearning();
    }

    componentWillUnmount() {
        this._unmounted = true;
        clearTimeout(this._learningTimer);
    }

    updateTeam() {
        new APIClient(API_URL + 'agency/team/').call('GET').then((data) => {
            if (this._unmounted) return;
            // `agency/team` es un `JsonResponse` plano, pero escribe `status: 'OK'` en el éxito, así que
            // la guarda por `status` es segura. Lo que no es OK es error: el backend manda
            // `ERROR_AGENCY` o `ERROR` (antes se comparaba contra un `ERROR_NO_AGENCY` que no manda
            // nunca, y sin `else` el spinner quedaba colgado), y el `APIClient` su `NETWORK_ERROR`.
            if (!isOk(data)) {
                if (this.state.team !== null) {
                    // Un refresco después de invitar o editar: la tabla que ya está sigue sirviendo.
                    toast.error(this.props.t('agency_team.load_error'));
                    return;
                }
                this.setState({loadError: (data && (data.error_code || data.status)) || 'ERROR'});
                return;
            }
            this.setState({
                team: data.team,
                invitations: data.invitations,
                canEditTeam: data.can_edit_team,
                canAssignClients: data.can_assign_clients,
                canAddClientUser: data.can_add_client_user !== false,
                canInviteClientUser: data.can_invite_client_user || false,
                isSuperAdmin: data.is_super_admin || false,
                moduleAccessConfigurationEnabled: data.module_access_configuration_enabled,
                clientUsersLimitInfo: data.client_users_limit_info || null
            });
        })
    }

    // El reintento vuelve al loader: sin eso el clic no se ve.
    retryLoad() {
        this.setState({loadError: null}, () => this.updateTeam());
    }

    /**
     * Las columnas de avance, sólo con la función prendida para quien mira (`enabled`) y sólo si es
     * Admin. Las dos condiciones salen del backend: `learning/me/` dice si está prendida y
     * `learning/team/` contesta OK sólo a un Admin. Un Regular ni lo pide.
     *
     * Es un fetch secundario: si falla, la pantalla queda como siempre. Mientras se resuelve, la tabla
     * espera, para no dibujar la clásica y cambiarla un segundo después.
     */
    loadLearning() {
        if (!this.props.isV2 || this._learningRequested) return;
        this._learningRequested = true;
        const request = this._learningRequest = {};
        // Pasado el tope, la lista se dibuja sin avance. Si el avance llega después, la tabla cambia:
        // mejor un cambio tardío que un Admin sin sus columnas.
        clearTimeout(this._learningTimer);
        this._learningTimer = setTimeout(() => {
            if (!this._unmounted && this._learningRequest === request) this.setState({learningResolved: true});
        }, LEARNING_WAIT_MS);
        // La respuesta de un pedido viejo (el usuario pasó al clásico mientras volaba) no se guarda.
        const current = () => !this._unmounted && this._learningRequest === request && this.props.isV2;
        LearnTeamApi.getAccess().then((access) => {
            if (!current()) return;
            if (!access || !access.enabled || !access.isAdmin) {
                this.setState({learningAccess: access, learningResolved: true});
                return;
            }
            this.setState({learningAccess: access});
            LearnTeamApi.getTeam().then((response) => {
                if (!current()) return;
                if (isOk(response) && response.data) {
                    this.setState({learning: response.data, learningResolved: true});
                    return;
                }
                this.setState({learningResolved: true});
                toast.error(this.props.t('agency_team.learning.load_error'));
            });
        });
    }

    forgetLearning() {
        clearTimeout(this._learningTimer);
        this._learningRequested = false;
        this._learningRequest = null;
        this.setState({learningAccess: null, learning: null, learningResolved: false});
    }

    // Después de invitar, borrar o editar a alguien: el resumen, la invitación nueva y el perfil cambian.
    // En segundo plano, sin avisar si falla (la tabla que ya está sigue sirviendo).
    refreshLearning() {
        if (!this.learningShown()) return;
        const request = this._learningRequest;
        LearnTeamApi.getTeam().then((response) => {
            if (this._unmounted || this._learningRequest !== request || !this.props.isV2) return;
            if (isOk(response) && response.data) this.setState({learning: response.data});
        });
    }

    learningPending() {
        return this.props.isV2 && !this.state.learningResolved;
    }

    // El avance se dibuja sólo en la 2.1: con el marco clásico, aunque haya quedado en el estado.
    learningShown() {
        return this.props.isV2 ? this.state.learning : null;
    }

    // El perfil al invitar acompaña a la función: con ella apagada no hay nada que ordenar.
    learningEnabled() {
        return !!(this.props.isV2 && this.state.learningAccess && this.state.learningAccess.enabled);
    }

    // El modal ya avisó con su toast: el `alert()` que seguía acá era un segundo aviso, y bloqueante.
    handleNewUserWasInvited() {
        this.updateTeam();
        this.refreshLearning();
    }

    reloadAfterEdit() {
        this.updateTeam();
        this.refreshLearning();
    }

    renderTeam() {
        if (this.state.loadError !== null) {
            const {t} = this.props;
            return (
                <LoadErrorState title={t('agency_team.load_error')}
                                text={t('agency_team.load_error_explanation')}
                                retryLabel={t('agency_team.load_error_retry')}
                                onRetry={this.retryLoad}/>
            );
        }
        if (this.state.team === null || this.state.invitations === null || this.learningPending()) {
            // El marcador le dice a la guía «aprende-equipo» que la lista todavía no está: espera en vez de
            // medir el ancla `team-members` como rota (review C-apr-fe-equipo-03).
            return (
                <div className="box-loader" {...guideLoadingAttr()}>
                    <Spinner color="positive"/>
                </div>
            )
        }
        // Ancla de la guía «aprende-equipo» (frente apr-guias): la lista del equipo, en las dos tablas.
        const membersAnchor = anchorAttr('team_members');
        const learning = this.learningShown();
        if (learning) {
            return (
                <>
                <TeamLearningHead summary={learning.summary}/>
                <TeamLearningTable guideAnchor={membersAnchor}
                                   team={this.state.team}
                                   invitations={this.state.invitations}
                                   learning={learning}
                                   showRole={!!this.showUserTypeColumn()}
                                   currentUserId={LocalStorage.fetchUserId()}
                                   renderRole={(user) => this.renderRoleContent(user)}
                                   renderInvitationRole={(invitation) => this.renderPendingRole(invitation)}
                                   renderUserOperation={(user) => this.renderUserOperation(user)}
                                   renderInvitationOperation={(invitation) => this.renderInvitationOperation(invitation)}/>
                </>
            );
        }
        return (
            <div className="table-box" {...membersAnchor}>
                <table className="table table-striped">
                    <thead>
                    <tr>
                        <th>{this.props.t('agency_team.user')}</th>
                        <th>{this.props.t('agency_team.email')}</th>
                        {this.renderUserTypeColumnHeader()}
                        <th/>
                    </tr>
                    </thead>
                    <tbody>
                    {this.renderUsers()}
                    {this.renderInvitations()}
                    </tbody>
                </table>
            </div>
        )
    }

    renderInviteTeam() {
        const modal = this.renderInviteModal();
        if (!modal) return null;
        // Ancla de la guía «aprende-equipo» (frente apr-guias): el botón de invitar. Va en un envoltorio
        // porque el botón lo dibuja el modal; el paso avanza igual con el clic, que se escucha en captura.
        return <div {...anchorAttr('team_invite')}>{modal}</div>;
    }

    renderInviteModal() {
        if (this.state.canEditTeam) {
            return (<InviteUserModal onUserInvited={this.handleNewUserWasInvited}
                                     moduleAccessConfigurationEnabled={this.state.moduleAccessConfigurationEnabled}
                                     canAddClientUser={this.state.canAddClientUser}
                                     clientUsersLimitInfo={this.state.clientUsersLimitInfo}
                                     isSuperAdmin={this.state.isSuperAdmin}
                                     showLearningProfile={this.learningEnabled()}/>);
        } else if (this.state.canInviteClientUser && this.state.canAssignClients) {
            return (<InviteUserModal onUserInvited={this.handleNewUserWasInvited}
                                     moduleAccessConfigurationEnabled={this.state.moduleAccessConfigurationEnabled}
                                     canAddClientUser={this.state.canAddClientUser}
                                     clientUsersLimitInfo={this.state.clientUsersLimitInfo}
                                     clientOnly={true}
                                     isSuperAdmin={this.state.isSuperAdmin}/>);
        }
        return null;
    }

    render() {
        const learning = this.learningShown();
        return (
            <section className="section-content">
                <AgencyHeader/>
                {this.renderTabs()}
                <div className={`section-body h-items${learning ? ' atl-body' : ''}`}>
                    <div className={`section-body-item -wider box agency-team-box${learning ? ' -learning' : ''}`}>
                        <header className="box-header">
                            <h2 className="box-title">{this.props.t('agency_team.team')}</h2>
                            {this.renderInviteTeam()}
                        </header>
                        {this.renderTeam()}
                    </div>
                </div>
            </section>
        )
    }

    renderTabs() {
        return <AgencyTabs activeTab="-second"/>
    }

    renderInvitationOperation(invitation) {
        if(this.state.canEditTeam)
            return <DeleteInvitationModal removeDeletedInvitation={this.removeDeletedInvitation} invitationId={invitation.id}/>
    }

    renderInvitations() {
        return this.state.invitations.map((invitation) => {
            return (
                <tr key={invitation.id}>
                    <td><p className="generic-alert">{this.props.t('agency_team.pending_invitation')}</p></td>
                    <td>{invitation.email}</td>
                    {this.renderInvitationTypeColumnData(invitation)}
                    <td className="action-cell">
                        {this.renderInvitationOperation(invitation)}
                    </td>
                </tr>
            );
        });
    }

    renderInvitationTypeColumnData(invitation) {
        if(!this.showUserTypeColumn())
            return;
        return (<td>{this.renderPendingRole(invitation)}</td>)
    }

    // Atenuado: el rol está definido pero todavía no está activo (invitación pendiente).
    renderPendingRole(invitation) {
        const content = this.renderRoleContent(invitation);
        if (!content) return null;
        return <div className="team-role-pending">{content}</div>;
    }

    renderClientUserOperation(user) {
        const canEdit = this.state.canEditTeam || this.state.canAssignClients;
        if (!canEdit) return null;
        return (
            <div>
                <div style={{display: "inline-block", verticalAlign: "middle"}}>
                    <EditClientFeaturesModal user={user} onSaved={this.reloadAfterEdit}/>
                </div>
                {this.state.canEditTeam && (
                    <div style={{display: "inline-block", verticalAlign: "middle", paddingLeft: "0.5em"}}>
                        <DeleteUserModal removeDeletedUser={this.removeDeletedUser} userId={user.id}/>
                    </div>
                )}
            </div>
        );
    }

    renderUserOperation(user) {
        if (user.user_type === 'Client') {
            return this.renderClientUserOperation(user);
        }
        if(this.state.canAssignClients && !this.state.canEditTeam) {
            return <div>
                <div style={{display: "inline-block", verticalAlign: "middle"}}>
                    <EditUserModalInline
                        user={user}
                        onSaved={this.reloadAfterEdit}
                        moduleAccessConfigurationEnabled={this.state.moduleAccessConfigurationEnabled}
                        canAddClientUser={this.state.canAddClientUser}
                        clientUsersLimitInfo={this.state.clientUsersLimitInfo}
                        isSuperAdmin={this.state.isSuperAdmin}
                    />
                </div>
            </div>
        }else if(LocalStorage.fetchUserId() === user.id || this.state.canEditTeam) {
            return (<div>
                <div style={{display: "inline-block", verticalAlign: "middle"}}>
                    <EditUserModalInline
                        user={user}
                        onSaved={this.reloadAfterEdit}
                        moduleAccessConfigurationEnabled={this.state.moduleAccessConfigurationEnabled}
                        canAddClientUser={this.state.canAddClientUser}
                        clientUsersLimitInfo={this.state.clientUsersLimitInfo}
                        isSuperAdmin={this.state.isSuperAdmin}
                    />
                </div>
                <div style={{display: "inline-block", verticalAlign: "middle", paddingLeft: "0.5em"}}>
                    <DeleteUserModal removeDeletedUser={this.removeDeletedUser} userId={user.id}/>
                </div>
            </div>)
        }
    }

    renderUsers() {
        return this.state.team.map((user) => {
            return (
                <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    {this.renderUserTypeColumnData(user)}
                    <td className="action-cell ">
                        {this.renderUserOperation(user)}
                    </td>
                </tr>
            );
        });
    }

    showUserTypeColumn() {
        return this.state.moduleAccessConfigurationEnabled;
    }

    // Con multi-cliente los badges son POR cliente (mapa `client_features_by_client` del
    // payload del equipo): mostrar solo los del primario mentía sobre los demás (F-M3).
    // Fallback: payload viejo sin el mapa, o un solo cliente → badges planos como siempre.
    renderClientFeatureBadgesForEntity(entity) {
        const byClient = entity.client_features_by_client;
        const ids = entity.client_ids || [];
        if (!byClient || ids.length <= 1) {
            return this.renderClientFeatureBadges(entity.client_features);
        }
        const names = entity.client_names || [];
        return ids.map((id, index) => {
            const badges = this.renderClientFeatureBadges(byClient[String(id)]);
            if (!badges) return null;
            return (
                <div key={id} className="team-client-features-row">
                    <span className="team-client-features-owner">{names[index] || id}</span>
                    {badges}
                </div>
            );
        });
    }

    renderClientFeatureBadges(features) {
        if (!features) return null;
        const { t } = this.props;
        // Orden estable + ícono por módulo (label corto para el chip).
        const featureMeta = [
            {key: 'dashboard', icon: 'chart-line', label: t('agency_team.module_dashboard')},
            {key: 'data_explorer', icon: 'binoculars', label: t('agency_team.module_data_explorer')},
            {key: 'reports', icon: 'file-lines', label: t('agency_team.module_reports')},
            {key: 'insights', icon: 'wand-magic-sparkles', label: t('agency_team.module_insights')},
            {key: 'connect', icon: 'robot', label: t('agency_team.module_connect')},
        ];
        const activeFeatures = featureMeta.filter(({key}) => features[key]);
        if (activeFeatures.length === 0) return null;
        return (
            <div className="team-client-features">
                {activeFeatures.map(({key, icon, label}) => (
                    <span key={key} className="team-feature-badge">
                        <FaIcon iconName={icon}/>{label}
                    </span>
                ))}
            </div>
        );
    }

    // El usuario del portal puede acceder a VARIOS clientes: primer nombre + badge "+N"
    // con tooltip listando el resto. `client_name` (singular) queda como fallback legacy.
    renderClientNames(entity) {
        const { t } = this.props;
        const names = (entity.client_names && entity.client_names.length > 0)
            ? entity.client_names
            : (entity.client_name ? [entity.client_name] : []);
        if (names.length === 0) return null;
        return (
            <span className="team-client-name">
                {names[0]}
                {names.length > 1 && (
                    <span className="info-icon-container team-client-more">
                        <span className="team-client-more-badge">+{names.length - 1}</span>
                        <div className="warning-tooltip -below">
                            <div className="warning-tooltip-body">
                                <p className="warning-tooltip-text">
                                    {t('agency_team.more_clients_tooltip', {
                                        clients: names.slice(1).join(', '),
                                        interpolation: {escapeValue: false},
                                    })}
                                </p>
                            </div>
                        </div>
                    </span>
                )}
            </span>
        );
    }

    // Render compartido entre usuarios activos e invitaciones pendientes
    // (ambos exponen user_type / client_features / client_name[s]).
    renderRoleContent(entity) {
        if (entity.user_type === "Client") {
            return (
                <div className="team-client-info">
                    <div className="team-client-header">
                        <RolePill userType={entity.user_type}/>
                        {this.renderClientNames(entity)}
                    </div>
                    {this.renderClientFeatureBadgesForEntity(entity)}
                </div>
            );
        }
        return <RolePill userType={entity.user_type}/>;
    }

    renderUserTypeColumnData(user) {
        if(!this.showUserTypeColumn())
            return;
        return (<td>{this.renderRoleContent(user)}</td>)
    }

    renderUserTypeColumnHeader() {
        if(!this.showUserTypeColumn())
            return;
        return (<th>{this.props.t('agency_team.role')}</th>)
    }

    refreshUserName(userData) {
        const teamWithModifiedUser = this.state.team.map((user) => {
            if (user.id === userData.userId) {
                return {
                    ...user,
                    name: userData.userName,
                }
            } else {
                return user
            }
        });
        this.setState({team: teamWithModifiedUser})
    }

    removeDeletedUser(userId) {
        const teamExcludingDeleted = this.state.team.filter((user) => {
            return (user.id !== userId);
        });
        this.setState({team: teamExcludingDeleted})
        // El resumen («N personas», «sin entrar…») contaba a quien se fue.
        this.refreshLearning();
    }

    removeDeletedInvitation(invitationId) {
        const invitationExcludingDeleted = this.state.invitations.filter((invitation) => {
            return (invitation.id !== invitationId);
        });
        this.setState({invitations: invitationExcludingDeleted})
    }
}

// Exportado para los tests: la pantalla sin el marco (`LayoutSidebar`) ni el router.
export const AgencyTeamContent = withTranslation()(AgencyTeamContentClass);

export default withRouterParams(AgencyTeam);